/*
 * Segundo bloque de envoltorios: los componentes de navegacion y presentacion que usan
 * las pantallas grandes. Cubre los 17 tipos de etiqueta que quedaron fuera del primer
 * bloque, el 23% restante de las 522 ocurrencias del proyecto.
 *
 * Igual que mz-form.ts: mismo selector y mismos @Input que ngx-materialize, para que
 * las plantillas no se toquen.
 */
import {
	AfterViewInit,
	Component,
	Directive,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	contentChild,
	inject,
} from '@angular/core';

declare const $: ((el: unknown) => Record<string, (arg?: unknown) => void>) | undefined;

function jq(el: Element | null): Record<string, (arg?: unknown) => void> | null {
	return el && typeof $ === 'function' ? $(el) : null;
}


/**
 * Materialize depende de relaciones de hijo directo: ul.collapsible > li,
 * li > .collapsible-header, nav > .nav-wrapper > ul > li. Un componente de Angular
 * siempre inserta su propio elemento anfitrion, que rompe esa relacion.
 *
 * Esta funcion saca de en medio esos elementos: sube los hijos al padre y elimina el
 * envoltorio. Es lo que permite conservar las plantillas intactas sin pelearse con el
 * CSS ni con el JS de la libreria.
 */
function desenvuelve(raiz: Element | null, selectores: string[]): void {
	if (!raiz) {
		return;
	}
	for (const sel of selectores) {
		for (const envoltorio of Array.from(raiz.querySelectorAll(sel))) {
			const padre = envoltorio.parentNode;
			if (!padre) {
				continue;
			}
			while (envoltorio.firstChild) {
				padre.insertBefore(envoltorio.firstChild, envoltorio);
			}
			padre.removeChild(envoltorio);
		}
	}
}

// ------------------------------------------------------------------ collapsible

@Component({
	selector: 'mz-collapsible',
	encapsulation: ViewEncapsulation.None,
	template: `<ul class="collapsible"><ng-content /></ul>`,
})
export class MzCollapsibleComponent implements AfterViewInit, OnDestroy {
	private observador?: MutationObserver;
	private reconstruyendo = false;
	private pendiente?: ReturnType<typeof setTimeout>;
	/** accordion o expandable, como lo nombra el DSL de la libreria anterior. */
	@Input() mode?: 'accordion' | 'expandable';
	@Input() accordion = false;
	@Input() popout = false;

	private readonly host = inject(ElementRef<HTMLElement>);

	/** El <ul> real sobre el que opera Materialize. */
	private get lista(): Element | null {
		return (this.host.nativeElement as HTMLElement).querySelector('ul.collapsible');
	}

	ngAfterViewInit(): void {
		const el = this.lista;
		if (!el) {
			return;
		}
		// data-collapsible se escribe aqui y no como binding del template: Materialize lo
		// lee una sola vez al inicializar, y asi no depende del orden en que Angular
		// resuelve los @Input contra el ngAfterViewInit del contenedor.
		el.setAttribute('data-collapsible', this.mode ?? (this.accordion ? 'accordion' : 'expandable'));
		if (this.popout) {
			el.classList.add('popout');
		}
		// Primero se quitan los elementos anfitriones que rompen ul > li, y despues se
		// liga. Va en una macrotarea porque los hijos proyectados aun no estan puestos
		// cuando corre el ngAfterViewInit del contenedor.
		setTimeout(() => this.reconstruye(el), 0);

		/*
		 * Y hay que RECONSTRUIRLO cuando cambien las secciones.
		 *
		 * Materialize recorre las cabeceras UNA VEZ al inicializar y se queda con esa
		 * lista. Las secciones del proyecto llegan con *ngIf / *ngFor cuando responde el
		 * backend, es decir despues de este ngAfterViewInit, asi que quedaba ligado solo
		 * a las que existieran en ese instante. Dos sintomas, una causa:
		 *
		 *  - "Expandir" abria una sola seccion: open(i) para i>0 no encontraba nada.
		 *  - Las secciones nuevas parecian bloqueadas al hacer clic: Materialize nunca
		 *    les puso su manejador, y encima conservaban su elemento anfitrion de
		 *    Angular en medio, que rompe la relacion `ul.collapsible > li`.
		 *
		 * Es el mismo defecto que tenia el select, y se arregla igual. Hace falta
		 * `subtree` porque los acordeones se anidan (bines-abc tiene uno dentro de otro),
		 * y por eso tambien la guarda: desenvuelve() muta ese mismo subarbol.
		 */
		this.observador = new MutationObserver(() => this.programaReconstruccion(el));
		this.observador.observe(el, { childList: true, subtree: true });
	}

	ngOnDestroy(): void {
		this.observador?.disconnect();
		if (this.pendiente !== undefined) {
			clearTimeout(this.pendiente);
		}
	}

	/** Agrupa las mutaciones de un mismo ciclo en una sola reconstruccion. */
	private programaReconstruccion(el: Element): void {
		if (this.reconstruyendo) {
			return;
		}
		if (this.pendiente !== undefined) {
			clearTimeout(this.pendiente);
		}
		this.pendiente = setTimeout(() => {
			this.pendiente = undefined;
			this.reconstruye(el);
		}, 0);
	}

	private reconstruye(el: Element): void {
		if (this.reconstruyendo) {
			return;
		}
		this.reconstruyendo = true;
		try {
			desenvuelve(el, ['mz-collapsible-item', 'mz-collapsible-item-header', 'mz-collapsible-item-body']);
			jq(el)?.['collapsible']?.();
		} finally {
			this.reconstruyendo = false;
		}
	}

	/**
	 * Abre la seccion n. El proyecto maneja el acordeon de bines-abc como un asistente
	 * por pasos: llama open/close con el indice de la seccion en 14 lugares.
	 */
	open(n: number): void {
		this.opera('open', n);
	}

	/** Cierra la seccion n. */
	close(n: number): void {
		this.opera('close', n);
	}

	private opera(accion: 'open' | 'close', n: number): void {
		const el = this.lista;
		if (!el || typeof $ !== 'function') {
			return;
		}
		// Materialize 0.100 recibe la accion y el indice como dos argumentos; la firma
		// tipada de jq() solo admite uno, asi que se invoca por la via sin tipar.
		const metodo = ($(el) as unknown as Record<string, (a: string, b: number) => void>)['collapsible'];
		if (typeof metodo === 'function') {
			metodo.call($(el), accion, n);
		}
	}
}

@Component({
	selector: 'mz-collapsible-item',
	encapsulation: ViewEncapsulation.None,
	template: `<li [class.active]="active"><ng-content /></li>`,
})
export class MzCollapsibleItemComponent {
	@Input() active = false;
}

@Component({
	selector: 'mz-collapsible-item-header',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="collapsible-header"><ng-content /></div>`,
})
export class MzCollapsibleItemHeaderComponent {}

@Component({
	selector: 'mz-collapsible-item-body',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="collapsible-body"><ng-content /></div>`,
})
export class MzCollapsibleItemBodyComponent {}

// ----------------------------------------------------------------------- navbar

@Component({
	selector: 'mz-navbar',
	encapsulation: ViewEncapsulation.None,
	template: `<nav><div class="nav-wrapper"><ng-content /></div></nav>`,
})
export class MzNavbarComponent {}

@Component({
	selector: 'mz-navbar-item-container',
	encapsulation: ViewEncapsulation.None,
	template: `<ul [class]="align + ' hide-on-med-and-down'"><ng-content /></ul>`,
})
export class MzNavbarItemContainerComponent {
	/** right o left; Materialize lo resuelve con la clase correspondiente. */
	@Input() align: 'right' | 'left' = 'right';
}

@Component({
	selector: 'mz-navbar-item',
	encapsulation: ViewEncapsulation.None,
	template: `<li [class.active]="active"><ng-content /></li>`,
})
export class MzNavbarItemComponent {
	@Input() active = false;
}

// ---------------------------------------------------------------------- sidenav

@Component({
	selector: 'mz-sidenav',
	encapsulation: ViewEncapsulation.None,
	template: `<ul [id]="id" class="side-nav" [class.fixed]="fixed"><ng-content /></ul>`,
})
export class MzSidenavComponent implements AfterViewInit {
	@Input() closeOnClick = false;
	@Input() id = 'sidenav';
	@Input() fixed = false;
	/** Id del elemento que abre el menu. En el navbar es 'btn-sidenav'. */
	@Input() collapseButtonId?: string;

	/*
	 * Los cuatro @Input estaban declarados y no se usaban para nada: el envoltorio
	 * pintaba el <ul class="side-nav"> y ahi se quedaba. Resultado: el menu no se
	 * desplegaba al pulsar el boton, sin ningun error.
	 *
	 * Materialize localiza el menu leyendo el atributo `data-activates` del DISPARADOR,
	 * no del menu:
	 *
	 *     var menuId = $this.attr('data-activates');
	 *
	 * Asi que hay dos cosas que hacer, y faltaban las dos: poner ese atributo en el
	 * boton que indica collapseButtonId, y llamar a sideNav() sobre el boton.
	 *
	 * Es el mismo patron que el select y el acordeon: un widget de Materialize que no
	 * funciona hasta que alguien lo inicializa de forma imperativa.
	 */
	ngAfterViewInit(): void {
		if (!this.collapseButtonId || typeof $ !== 'function') {
			return;
		}
		const boton = document.getElementById(this.collapseButtonId);
		if (!boton) {
			return;
		}
		boton.setAttribute('data-activates', this.id);
		const jqBoton = jq(boton);
		const sideNav = jqBoton?.['sideNav'];
		if (typeof sideNav === 'function') {
			sideNav.call(jqBoton, { closeOnClick: this.closeOnClick });
		}
	}
}

@Component({
	selector: 'mz-sidenav-header',
	encapsulation: ViewEncapsulation.None,
	template: `<li><div class="userView"><ng-content /></div></li>`,
})
export class MzSidenavHeaderComponent {}

@Component({
	selector: 'mz-sidenav-link',
	encapsulation: ViewEncapsulation.None,
	template: `<li [class.active]="active"><ng-content /></li>`,
})
export class MzSidenavLinkComponent {
	@Input() active = false;
}

@Component({
	selector: 'mz-sidenav-divider',
	encapsulation: ViewEncapsulation.None,
	template: `<li><div class="divider"></div></li>`,
})
export class MzSidenavDividerComponent {}

// ------------------------------------------------------------------- collection

@Component({
	selector: 'mz-collection',
	encapsulation: ViewEncapsulation.None,
	template: `<ul class="collection"><ng-content /></ul>`,
})
export class MzCollectionComponent {
	@Input() header?: string;
}

@Component({
	selector: 'mz-collection-item',
	encapsulation: ViewEncapsulation.None,
	template: `<li class="collection-item" [class.active]="active"><ng-content /></li>`,
})
export class MzCollectionItemComponent {
	@Input() active = false;
}

// ------------------------------------------------------------------------ badge

@Component({
	selector: 'mz-badge',
	encapsulation: ViewEncapsulation.None,
	template: `<span class="badge" [class.new]="isNew" [attr.data-badge-caption]="caption"><ng-content /></span>`,
})
export class MzBadgeComponent {
	@Input() isNew = false;
	@Input() caption?: string;
}

// ----------------------------------------------------------------------- switch

@Component({
	selector: 'mz-switch-container',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="switch" [class]="className"><label>{{ offLabel }}<ng-content />{{ onLabel }}</label></div>`,
})
export class MzSwitchContainerComponent {
	@Input() offLabel = '';
	@Input() onLabel = '';
	/** search-ejecutor liga [className] para el color del semaforo. */
	@Input() className = '';
}

/**
 * input[mz-switch]: 1 uso, en search-ejecutor.component.html:382.
 *
 * Dos defectos del envoltorio anterior que este arregla:
 *
 * 1. Las etiquetas. La plantilla original las liga en el <input> -[off]="'Bloq'"
 *    [on]="'Desb'"- y el contenedor las esperaba como offLabel/onLabel propios. Angular
 *    SI rechaza una propiedad ligada desconocida, asi que esto habria tronado al portar
 *    search-ejecutor; es el unico de los tres envoltorios faltantes que falla ruidoso.
 * 2. Faltaba el <span class="lever">, que es lo que Materialize dibuja como el switch.
 *    Sin el no se ve un switch, se ve una casilla suelta.
 *
 * Se resuelve desde el input porque es quien tiene los valores, y asi no hay que
 * coordinar padre e hijo ni tocar la plantilla.
 */
@Directive({ selector: 'input[mz-switch]' })
export class MzSwitchDirective implements AfterViewInit {
	@Input() off = '';
	@Input() on = '';

	private readonly host = inject<ElementRef<HTMLInputElement>>(ElementRef);

	ngAfterViewInit(): void {
		const input = this.host.nativeElement;
		// El datatable recicla filas; el guardia es por elemento, no por instancia.
		if (!input.parentElement || input.dataset['mzSwitch'] === 'listo') {
			return;
		}
		input.type = 'checkbox';
		if (this.off) {
			input.parentElement.insertBefore(document.createTextNode(this.off), input);
		}
		const lever = document.createElement('span');
		lever.className = 'lever';
		input.after(lever);
		if (this.on) {
			lever.after(document.createTextNode(this.on));
		}
		input.dataset['mzSwitch'] = 'listo';
	}
}

/**
 * [mz-secondary-content]: 6 usos en modal-diff. Alinea a la derecha dentro de un
 * mz-collection-item. Atributo plano, asi que tampoco lo detectaba el compilador.
 */
@Directive({
	selector: '[mz-secondary-content]',
	host: { class: 'secondary-content' },
})
export class MzSecondaryContentDirective {}

// ------------------------------------------------------------------- pagination

@Component({
	selector: 'mz-pagination',
	encapsulation: ViewEncapsulation.None,
	template: `<ul class="pagination"><ng-content /></ul>`,
})
export class MzPaginationComponent {}

// --------------------------------------------------------------------- tooltip

/**
 * Directiva de tooltip. Materialize la activa por los atributos data-position,
 * data-delay y data-tooltip, y luego hay que pedirle que inicialice.
 */
@Directive({ selector: '[tooltip], [mz-tooltip]' })
export class MzTooltipDirective implements OnInit, OnDestroy {
	@Input() tooltip?: string;
	@Input() position?: string;
	@Input() delay?: number;
	@Input() html?: string;

	private readonly host = inject(ElementRef<HTMLElement>);

	ngOnInit(): void {
		const el = this.host.nativeElement as HTMLElement;
		el.classList.add('tooltipped');
		el.setAttribute('data-tooltip', this.html ?? this.tooltip ?? '');
		if (this.position) {
			el.setAttribute('data-position', this.position);
		}
		if (this.delay != null) {
			el.setAttribute('data-delay', String(this.delay));
		}
		jq(el)?.['tooltip']?.({ delay: this.delay ?? 50 });

		/*
		 * Ocultarlo al hacer clic.
		 *
		 * Materialize lo muestra en `mouseenter.tooltip` y lo oculta en
		 * `mouseleave.tooltip`. Si al pulsar el boton se abre un modal o se navega, el
		 * puntero deja de estar sobre el elemento sin que llegue a dispararse el
		 * mouseleave, y el globo se queda colgado en pantalla.
		 *
		 * Se dispara el mouseleave a mano para que corra la animacion de ocultado de
		 * Materialize, en vez de borrar el elemento por nuestra cuenta.
		 */
		el.addEventListener('click', this.alPulsar);
	}

	/*
	 * Y destruirlo al destruir el elemento.
	 *
	 * El globo NO es hijo del disparador: Materialize lo crea aparte, lo cuelga del
	 * <body> y los enlaza con el atributo `data-tooltip-id`. Cuando Angular destruye el
	 * boton -al repintar una fila del datatable, al navegar, al cerrar un modal- ese
	 * globo se queda huerfano en el <body> para siempre. Era el sintoma: el label
	 * "editar" seguia en pantalla al volver o al cancelar.
	 *
	 * `tooltip('remove')` hace la limpieza completa: borra el globo por su id, quita el
	 * atributo y desengancha los dos manejadores.
	 */
	ngOnDestroy(): void {
		const el = this.host.nativeElement as HTMLElement;
		el.removeEventListener('click', this.alPulsar);
		jq(el)?.['tooltip']?.('remove');
	}

	private readonly alPulsar = (): void => {
		const el = this.host.nativeElement as HTMLElement;
		jq(el)?.['trigger']?.('mouseleave.tooltip');
	};
}

// ------------------------------------------------------------------- datepicker

/**
 * Contenedor de fecha. Materialize 0.100 usa pickadate; se inicializa sobre el input
 * con las opciones que el componente declara como Pickadate.DateOptions.
 */
@Directive({ selector: 'input[mz-datepicker]' })
export class MzDatepickerDirective implements AfterViewInit, OnDestroy {
	@Input() label?: string;
	@Input() options?: Record<string, unknown>;

	readonly host = inject(ElementRef<HTMLElement>);

	private propagando = false;

	get controlId(): string {
		return (this.host.nativeElement as HTMLElement).getAttribute('id') ?? '';
	}

	ngAfterViewInit(): void {
		const el = this.host.nativeElement as HTMLInputElement;
		el.classList.add('datepicker');
		jq(el)?.['pickadate']?.(this.options ?? {});

		/*
		 * Puente del evento, igual que en el select y por el mismo motivo.
		 *
		 * pickadate escribe la fecha y la propaga con el trigger de jQuery:
		 *
		 *     $ELEMENT.val(P.get(thingItem, SETTINGS.format)).trigger('change');
		 *
		 * `.trigger()` ejecuta los manejadores de jQuery y NO emite un evento DOM, asi
		 * que ni el ControlValueAccessor de [(ngModel)] ni un (change) de plantilla se
		 * enteran: el usuario elige la fecha, la ve en el campo, y el modelo sigue vacio.
		 * Los filtros de busqueda saldrian sin fecha.
		 *
		 * Se reemite como evento nativo. La guarda de reentrada es necesaria porque
		 * jQuery escucha por addEventListener y tambien oiria lo que acabamos de emitir.
		 */
		const jqEl = jq(el);
		jqEl?.['off']?.('change.mzPuente');
		const on = jqEl?.['on'] as ((e: string, h: () => void) => void) | undefined;
		on?.call(jqEl, 'change.mzPuente', () => {
			if (this.propagando) {
				return;
			}
			this.propagando = true;
			try {
				el.dispatchEvent(new Event('change', { bubbles: true }));
				el.dispatchEvent(new Event('input', { bubbles: true }));
			} finally {
				this.propagando = false;
			}
		});
	}

	/*
	 * pickadate crea su propio calendario y lo inserta como hermano del input
	 * (`$ELEMENT.before(P.$root)`), mas un campo oculto y sus manejadores. `stop()` lo
	 * desmonta todo: cierra, borra el root y el oculto, quita clases y datos, y
	 * desengancha los eventos.
	 *
	 * Sin esto queda estado de pickadate por cada input destruido, y en este proyecto los
	 * filtros con fecha se repintan cada vez que cambia una seccion.
	 */
	ngOnDestroy(): void {
		const el = this.host.nativeElement as HTMLElement;
		const jqEl = jq(el);
		jqEl?.['off']?.('change.mzPuente');
		try {
			const pickadate = jqEl?.['pickadate'] as unknown as ((a: string) => { stop?: () => void }) | undefined;
			pickadate?.call(jqEl, 'picker')?.stop?.();
		} catch {
			// Si nunca se inicializo, no hay nada que detener.
		}
	}
}

/*
 * Contenedor del datepicker.
 *
 * Antes era solo `<div class="input-field"><ng-content /></div>`, es decir ignoraba el
 * [label] por completo. Las plantillas lo declaran en el input:
 *
 *     <mz-datepicker-container>
 *       <i class="material-icons prefix">date_range</i>
 *       <input mz-input mz-datepicker [label]="'FECHA INICIO'" ...>
 *     </mz-datepicker-container>
 *
 * Asi que los 12 campos de fecha del proyecto se quedaban sin su etiqueta. Se replica lo
 * que hace mz-input-container: localizar el control proyectado y pintar su <label>.
 *
 * La etiqueta va DESPUES del <ng-content>, como en el contenedor de input, porque
 * Materialize posiciona la etiqueta flotante con `.input-field label` y el selector de
 * prefijo `.prefix ~ label` necesita que sea hermana posterior.
 */
@Component({
	selector: 'mz-datepicker-container',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="input-field">
			<ng-content />
			@if (etiqueta) {
				<label [attr.for]="idControl" [innerHTML]="etiqueta"></label>
			}
		</div>
	`,
})
export class MzDatepickerContainerComponent {
	private readonly control = contentChild(MzDatepickerDirective);

	get etiqueta(): string {
		return this.control()?.label ?? '';
	}

	get idControl(): string {
		return this.control()?.controlId ?? '';
	}
}

export const MZ_LAYOUT = [
	MzCollapsibleComponent,
	MzCollapsibleItemComponent,
	MzCollapsibleItemHeaderComponent,
	MzCollapsibleItemBodyComponent,
	MzNavbarComponent,
	MzNavbarItemContainerComponent,
	MzNavbarItemComponent,
	MzSidenavComponent,
	MzSidenavHeaderComponent,
	MzSidenavLinkComponent,
	MzSidenavDividerComponent,
	MzCollectionComponent,
	MzCollectionItemComponent,
	MzBadgeComponent,
	MzSwitchContainerComponent,
	MzSwitchDirective,
	MzSecondaryContentDirective,
	MzPaginationComponent,
	MzTooltipDirective,
	MzDatepickerDirective,
	MzDatepickerContainerComponent,
] as const;
